import { DateTimeHelper } from '@absg/angular-platform';
import { ClinicChargeItemViewModel, InventoryItem, ManagePosition, PackingSlipDetail, PatientDispenseItemViewModel, Position,
PositionAssignment, PurchaseOrderDetailViewModel, UnassignBin } from '@absg/nim-client-shared';
import { TransferNDCLotDetail } from 'core.models/transfer-ndc-lot-detail.model';
import { ItemPillExtendedView } from 'shared.root/models/Item-pill-extendedview.model';
import { WitnessItem } from 'shared.root/models/witness-base.model';

export class ItemPillExtendedViewHelper {

  static convertPatientDispenseItemViewModelToItemPillExtendedView(item: PatientDispenseItemViewModel): ItemPillExtendedView {
    const pillItem = new ItemPillExtendedView();
    pillItem.itemId = item.itemId;
    pillItem.name = item.displayName;
    pillItem.ndc = item.ndc;
    pillItem.formularyTypeId = item.formularyTypeId;
    pillItem.is340B = item.is340BStatus();
    pillItem.isBuyIn = item.isBuyInStatus();
    pillItem.isWitness = item?.assignedPosition?.isWitnessRequired;
    pillItem.isReturned = item.isReturnedStatus();
    pillItem.isWasted = item.isWastedStatus();
    pillItem.patientOwnedId = item.patientOwnedPatientId;
    pillItem.isDiscontinued = item.isDiscontinuedStatus();
    pillItem.isFluVaccine = item.isFluVaccineStatus();
    pillItem.isFormularyUnAssigned = item.isFormularyUnAssignedStatus();
    pillItem.isInsufficientQuantity = item.isInsufficientQuantityStatus();
    pillItem.isItem505b2 = item.isItem505b2Status();
    pillItem.isUnassigned = item.isUnassignedStatus();
    pillItem.isPracticeUnAssigned = item.isPracticeUnAssignedStatus();
    pillItem.isPracticeFavorite = item.isPracticeFavorite;
    pillItem.isRefrigerated = item.isRefrigerated;
    pillItem.isBin = item.isBinned();
    pillItem.isAuditFavorite = false;
    pillItem.suppressBinIcon = true;
    pillItem.patientOwnedDemographics = item.getDispenseSnapshotPatientOwnedDemographics();
    pillItem.patientOwnedItemTypeShortName = item.patientOwnedItemTypeShortName;
    return pillItem;
  }

  static convertManagePositionToItemPillExtendedView(item: ManagePosition): ItemPillExtendedView {
    const pillItem = new ItemPillExtendedView();
    pillItem.itemId = item.productDetail.itemId;
    pillItem.name = item.productDetail.displayName;
    pillItem.ndc = item.productDetail.unitNdc;
    pillItem.formularyTypeId = item.productDetail.formularyTypeId;
    pillItem.is340B = item.is340BStatus();
    pillItem.isBuyIn = item.isBuyInStatus();
    pillItem.isWitness = item.witnessRequired;
    pillItem.isReturned = item.isReturnedStatus();
    pillItem.isWasted = item.isWastedStatus();
    pillItem.patientOwnedId = item?.inventoryItem?.patientId;
    pillItem.isDiscontinued = item.isDiscontinuedStatus();
    pillItem.isFluVaccine = item.isFluVaccineStatus();
    pillItem.isFormularyUnAssigned = item.isFormularyUnAssignedStatus();
    pillItem.isInsufficientQuantity = item.isInsufficientQuantityStatus();
    pillItem.isItem505b2 = item.isItem505b2Status();
    pillItem.isUnassigned = item.isUnassignedStatus();
    pillItem.isPracticeUnAssigned = item.isPracticeUnAssignedStatus();
    pillItem.isPracticeFavorite = item.productDetail.isPracticeFavorite;
    pillItem.isRefrigerated = item.productDetail.refrigerated;
    pillItem.isBin = item.isBinned();
    pillItem.isAuditFavorite = false;
    pillItem.suppressBinIcon = true;
    pillItem.patientOwnedDemographics = item.getPatientOwnedDemographics();
    pillItem.patientOwnedItemTypeShortName = item.patientOwnedItemTypeShortName;
    return pillItem;
  }

  static convertPositionAssignmentToItemPillExtendedView(item: PositionAssignment): ItemPillExtendedView {
    const pillItem = new ItemPillExtendedView();
    pillItem.itemId = item.formularyItemNumber;
    pillItem.name = item.formularyDisplayName;
    pillItem.ndc = item.formularyItemNdc;
    pillItem.formularyTypeId = item.formularyTypeId;
    pillItem.is340B = item.is340BStatus();
    pillItem.isBuyIn = item.isBuyInStatus();
    pillItem.isWitness = item.isWitnessRequired;
    pillItem.isReturned = item.isReturnedStatus();
    pillItem.isWasted = item.isWastedStatus();
    pillItem.patientOwnedId = item?.patientInfo?.patientId;
    pillItem.isDiscontinued = item.isDiscontinuedStatus();
    pillItem.isFluVaccine = item.isFluVaccineStatus();
    pillItem.isFormularyUnAssigned = item.isFormularyUnAssignedStatus();
    pillItem.isInsufficientQuantity = item.isInsufficientQuantityStatus();
    pillItem.isItem505b2 = item.isItem505b2Status();
    pillItem.isUnassigned = item.isUnassignedStatus();
    pillItem.isPracticeUnAssigned = item.isPracticeUnAssignedStatus();
    pillItem.isPracticeFavorite = item.formularyIsFavorite;
    pillItem.isRefrigerated = item.formularyIsRefrigerated;
    pillItem.isBin = item.isBinned();
    pillItem.isAuditFavorite = false;
    pillItem.suppressBinIcon = true;
    pillItem.patientOwnedDemographics = `${item.patientInfo?.lastName}, ${item.patientInfo?.firstName} (${item.patientInfo?.idNumber}) ${DateTimeHelper.formatDate(item.patientInfo?.birthDate)}`;
    pillItem.patientOwnedItemTypeShortName = item.patientOwnedItemTypeShortName;
    return pillItem;
  }

  static convertUnassignBinToItemPillExtendedView(item: UnassignBin, isWitnessRequired: boolean): ItemPillExtendedView {
    const pillItem = new ItemPillExtendedView();
    pillItem.itemId = item.itemId;
    pillItem.name = item.displayName;
    pillItem.ndc = item.unitNdc;
    pillItem.formularyTypeId = item.formularyTypeId;
    pillItem.is340B = item.is340BStatus();
    pillItem.isBuyIn = item.isBuyInStatus();
    pillItem.isWitness = isWitnessRequired;
    pillItem.isReturned = item.isReturnedStatus();
    pillItem.isWasted = item.isWastedStatus();
    pillItem.patientOwnedId = null;
    pillItem.isDiscontinued = item.isDiscontinuedStatus();
    pillItem.isFluVaccine = item.isFluVaccineStatus();
    pillItem.isFormularyUnAssigned = item.isFormularyUnAssignedStatus();
    pillItem.isInsufficientQuantity = item.isInsufficientQuantityStatus();
    pillItem.isItem505b2 = item.isItem505b2Status();
    pillItem.isUnassigned = item.isUnassignedStatus();
    pillItem.isPracticeUnAssigned = item.isPracticeUnAssignedStatus();
    pillItem.isPracticeFavorite = item.isPracticeFavorite;
    pillItem.isRefrigerated = item.isRefrigerated;
    pillItem.isBin = item.isBinned();
    pillItem.isAuditFavorite = false;
    pillItem.suppressBinIcon = true;
    pillItem.patientOwnedDemographics = null;
    pillItem.patientOwnedItemTypeShortName = null;
    return pillItem;
  }

  static convertTransferNDClotDetailToItemPillExtendedView(item: TransferNDCLotDetail): ItemPillExtendedView {
    const pillItem = new ItemPillExtendedView();
    pillItem.itemId = item.itemId;
    pillItem.name = item.displayName;
    pillItem.ndc = item.unitNdc;
    pillItem.formularyTypeId = item.formularyTypeId;
    pillItem.is340B = item.is340BStatus();
    pillItem.isBuyIn = item.isBuyInStatus();
    pillItem.isWitness = false;
    pillItem.isReturned = item.isReturnedStatus();
    pillItem.isWasted = item.isWastedStatus();
    pillItem.patientOwnedId = null;
    pillItem.isDiscontinued = item.isDiscontinuedStatus();
    pillItem.isFluVaccine = item.isFluVaccineStatus();
    pillItem.isFormularyUnAssigned = item.isFormularyUnAssignedStatus();
    pillItem.isInsufficientQuantity = item.isInsufficientQuantityStatus();
    pillItem.isItem505b2 = item.isItem505b2Status();
    pillItem.isUnassigned = item.isUnassignedStatus();
    pillItem.isPracticeUnAssigned = item.isPracticeUnAssignedStatus();
    pillItem.isPracticeFavorite = false;
    pillItem.isRefrigerated = false;
    pillItem.isBin = true;
    pillItem.isAuditFavorite = false;
    pillItem.suppressBinIcon = true;
    pillItem.patientOwnedDemographics = null;
    pillItem.patientOwnedItemTypeShortName = null;
    return pillItem;
  }

  static convertInventoryItemToItemPillExtendedView(item: InventoryItem): ItemPillExtendedView {
    const pillItem = new ItemPillExtendedView();
    pillItem.itemId = item.itemId;
    pillItem.name = item.getDisplayName();
    pillItem.ndc = item.ndc;
    pillItem.formularyTypeId = item.formularyTypeId;
    pillItem.is340B = item.is340BStatus();
    pillItem.isBuyIn = item.isBuyInStatus();
    pillItem.isWitness = item.witnessRequired;
    pillItem.isReturned = item.isReturnedStatus();
    pillItem.isWasted = item.isWastedStatus();
    pillItem.patientOwnedId = item.patientId;
    pillItem.isDiscontinued = item.isDiscontinuedStatus();
    pillItem.isFluVaccine = item.isFluVaccineStatus();
    pillItem.isFormularyUnAssigned = item.isFormularyUnAssignedStatus();
    pillItem.isInsufficientQuantity = item.isInsufficientQuantityStatus();
    pillItem.isItem505b2 = item.isItem505b2Status();
    pillItem.isUnassigned = item.isUnassignedStatus();
    pillItem.isPracticeUnAssigned = item.isPracticeUnAssignedStatus();
    pillItem.isPracticeFavorite = item.isPracticeFavorite;
    pillItem.isRefrigerated = item.refrigerated;
    pillItem.isBin = item.isBinned();
    pillItem.isAuditFavorite = false;
    pillItem.suppressBinIcon = true;
    pillItem.patientOwnedDemographics = item.getPatientOwnedDemographics();
    pillItem.patientOwnedItemTypeShortName = item.patientOwnedItemTypeShortName;
    return pillItem;
  }

  static convertPatientDispenseItemVMToItemPillExtendedView(item: PatientDispenseItemViewModel): ItemPillExtendedView {
    const pillItem = new ItemPillExtendedView();
    pillItem.itemId = item.itemId;
    pillItem.name = item.displayName;
    pillItem.ndc = item.ndc;
    pillItem.formularyTypeId = item.formularyTypeId;
    pillItem.is340B = item.is340BStatus();
    pillItem.isBuyIn = item.isBuyInStatus();
    pillItem.isWitness = item.isWitnessRequiredStatus();
    pillItem.isReturned = item.isReturnedStatus();
    pillItem.isWasted = item.isWastedStatus();
    pillItem.patientOwnedId = item.patientOwnedPatientId;
    pillItem.isDiscontinued = item.isDiscontinuedStatus();
    pillItem.isFluVaccine = item.isFluVaccineStatus();
    pillItem.isFormularyUnAssigned = item.isFormularyUnAssignedStatus();
    pillItem.isInsufficientQuantity = item.isInsufficientQuantityStatus();
    pillItem.isItem505b2 = item.isItem505b2Status();
    pillItem.isUnassigned = item.isUnassignedStatus();
    pillItem.isPracticeUnAssigned = item.isPracticeUnAssignedStatus();
    pillItem.isPracticeFavorite = item.isPracticeFavorite;
    pillItem.isRefrigerated = item.isRefrigerated;
    pillItem.isBin = item.isBinned();
    pillItem.isAuditFavorite = false;
    pillItem.suppressBinIcon = true;
    pillItem.patientOwnedDemographics = item.getDispenseSnapshotPatientOwnedDemographics();
    pillItem.patientOwnedItemTypeShortName = item.patientOwnedItemTypeShortName;
    return pillItem;
  }

  static convertWitnessItemToItemPillExtendedView(item: WitnessItem): ItemPillExtendedView {
    const pillItem = new ItemPillExtendedView();
    pillItem.itemId = item.itemId;
    pillItem.name = item.displayName;
    pillItem.ndc = item.ndc;
    pillItem.formularyTypeId = item.formularyTypeId;
    pillItem.is340B = item.is340BStatus();
    pillItem.isBuyIn = item.isBuyInStatus();
    pillItem.isWitness = true;
    pillItem.isReturned = item.isReturnedStatus();
    pillItem.isWasted = item.isWastedStatus();
    pillItem.patientOwnedId = item.patientInfo?.patientId;
    pillItem.isDiscontinued = item.isDiscontinuedStatus();
    pillItem.isFluVaccine = item.isFluVaccineStatus();
    pillItem.isFormularyUnAssigned = item.isFormularyUnAssignedStatus();
    pillItem.isInsufficientQuantity = item.isInsufficientQuantityStatus();
    pillItem.isItem505b2 = item.isItem505b2Status();
    pillItem.isUnassigned = item.isUnassignedStatus();
    pillItem.isPracticeUnAssigned = item.isPracticeUnAssignedStatus();
    pillItem.isPracticeFavorite = item.isPracticeFavorite;
    pillItem.isRefrigerated = item.isRefrigerated;
    pillItem.isBin = item.isBinned();
    pillItem.isAuditFavorite = false;
    pillItem.suppressBinIcon = true;
    pillItem.patientOwnedDemographics = item.getPatientOwnedDemographics();
    pillItem.patientOwnedItemTypeShortName = item.patientOwnedItemTypeShortName;
    return pillItem;
  }

  static convertClinicChargeItemVMToItemPillExtendedView(item: ClinicChargeItemViewModel): ItemPillExtendedView {
    const pillItem = new ItemPillExtendedView();
    pillItem.itemId = item.itemId;
    pillItem.name = item.displayName;
    pillItem.ndc = item.unitNdc;
    pillItem.formularyTypeId = item.formularyTypeId;
    pillItem.is340B = item.is340BStatus();
    pillItem.isBuyIn = item.isBuyInStatus();
    pillItem.isWitness = item.isWitnessRequired;
    pillItem.isReturned = item.isReturnedStatus();
    pillItem.isWasted = item.isWastedStatus();
    pillItem.patientOwnedId = null;
    pillItem.isDiscontinued = item.isDiscontinuedStatus();
    pillItem.isFluVaccine = item.isFluVaccineStatus();
    pillItem.isFormularyUnAssigned = item.isFormularyUnAssignedStatus();
    pillItem.isInsufficientQuantity = item.isInsufficientQuantityStatus();
    pillItem.isItem505b2 = item.isItem505b2Status();
    pillItem.isUnassigned = item.isUnassignedStatus();
    pillItem.isPracticeUnAssigned = item.isPracticeUnAssignedStatus();
    pillItem.isPracticeFavorite = item.isPracticeFavorite;
    pillItem.isRefrigerated = item.isRefrigerated;
    pillItem.isBin = item.isBinned();
    pillItem.isAuditFavorite = false;
    pillItem.suppressBinIcon = true;
    pillItem.patientOwnedDemographics = null;
    pillItem.patientOwnedItemTypeShortName = null;
    return pillItem;
  }

  static convertPurchaseOrderDetailVMToItemPillExtendedView(item: PurchaseOrderDetailViewModel): ItemPillExtendedView {
    const pillItem = new ItemPillExtendedView();
    pillItem.itemId = item.itemId;
    pillItem.name = item.displayName;
    pillItem.ndc = item.ndc;
    pillItem.formularyTypeId = item.formularyTypeId;
    pillItem.is340B = item.is340BStatus();
    pillItem.isBuyIn = item.isBuyInStatus();
    pillItem.isWitness = item.witnessRequired;
    pillItem.isReturned = item.isReturnedStatus();
    pillItem.isWasted = item.isWastedStatus();
    pillItem.patientOwnedId = null;
    pillItem.isDiscontinued = item.isDiscontinuedStatus();
    pillItem.isFluVaccine = item.isFluVaccineStatus();
    pillItem.isFormularyUnAssigned = item.isFormularyUnAssignedStatus();
    pillItem.isInsufficientQuantity = item.isInsufficientQuantityStatus();
    pillItem.isItem505b2 = item.isItem505b2Status();
    pillItem.isUnassigned = item.isUnassignedStatus();
    pillItem.isPracticeUnAssigned = item.isPracticeUnAssignedStatus();
    pillItem.isPracticeFavorite = item.isItemPracticeFavorite();
    pillItem.isRefrigerated = item.isItemRefrigerated();
    pillItem.isBin = item.isBinned();
    pillItem.isAuditFavorite = false;
    pillItem.suppressBinIcon = true;
    pillItem.patientOwnedDemographics = null;
    pillItem.patientOwnedItemTypeShortName = null;
    return pillItem;
  }

  static convertPackingSlipDetailToItemPillExtendedView(item: PackingSlipDetail): ItemPillExtendedView {
    const pillItem = new ItemPillExtendedView();
    pillItem.itemId = item.productDetail.itemId;
    pillItem.name = item.productDetail.displayName;
    pillItem.ndc = item.productDetail.unitNdc;
    pillItem.formularyTypeId = item.productDetail.formularyTypeId;
    pillItem.is340B = item.is340BStatus();
    pillItem.isBuyIn = item.isBuyInStatus();
    pillItem.isWitness = item.isWitness;
    pillItem.isReturned = item.isReturnedStatus();
    pillItem.isWasted = item.isWastedStatus();
    pillItem.patientOwnedId = null;
    pillItem.isDiscontinued = item.isDiscontinuedStatus();
    pillItem.isFluVaccine = item.isFluVaccineStatus();
    pillItem.isFormularyUnAssigned = item.isFormularyUnAssignedStatus();
    pillItem.isInsufficientQuantity = item.isInsufficientQuantityStatus();
    pillItem.isItem505b2 = item.isItem505b2Status();
    pillItem.isUnassigned = item.isUnassignedStatus();
    pillItem.isPracticeUnAssigned = item.isPracticeUnAssignedStatus();
    pillItem.isPracticeFavorite = item.productDetail.isPracticeFavorite;
    pillItem.isRefrigerated = item.isRefrigerated;
    pillItem.isBin = item.isBinned();
    pillItem.isAuditFavorite = false;
    pillItem.suppressBinIcon = true;
    pillItem.patientOwnedDemographics = null;
    pillItem.patientOwnedItemTypeShortName = null;
    return pillItem;
  }

  static convertTransferDetailToItemPillExtendedView(item: Position): ItemPillExtendedView {
    const pillItem = new ItemPillExtendedView();
    pillItem.itemId = item.products[0].detail.itemId;
    pillItem.name = item.products[0].detail.displayName;
    pillItem.ndc = item.products[0].detail.unitNdc;
    pillItem.formularyTypeId = item.products[0].detail.formularyTypeId;
    pillItem.is340B = item.is340BStatus();
    pillItem.isBuyIn = item.isBuyInStatus();
    pillItem.isWitness = item.isWitnessRequired;
    pillItem.isReturned = item.isReturnedStatus();
    pillItem.isWasted = item.isWastedStatus();
    pillItem.patientOwnedId = item.patientInfo?.patientId;
    pillItem.isDiscontinued = item.isDiscontinuedStatus();
    pillItem.isFluVaccine = item.isFluVaccineStatus();
    pillItem.isFormularyUnAssigned = item.isFormularyUnAssignedStatus();
    pillItem.isInsufficientQuantity = item.isInsufficientQuantityStatus();
    pillItem.isItem505b2 = item.isItem505b2Status();
    pillItem.isUnassigned = item.isUnassignedStatus();
    pillItem.isPracticeUnAssigned = item.isPracticeUnAssignedStatus();
    pillItem.isPracticeFavorite = item.products[0].detail.isPracticeFavorite;
    pillItem.isRefrigerated = item.isItemRefrigerated();
    pillItem.isBin = item.isBinned();
    pillItem.isAuditFavorite = false;
    pillItem.suppressBinIcon = true;
    pillItem.patientOwnedDemographics = null;
    pillItem.patientOwnedItemTypeShortName = null;
    return pillItem;
  }
}
